import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Users, Plus, Hash, Mail, KeyRound, Copy, Check } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const ROLES = [
    { v: "admin", l: "Admin (e-mail)" },
    { v: "ufficio", l: "Ufficio Produzione" },
    { v: "operatore", l: "Operatore Saldatura" },
    { v: "operatore_estrusione", l: "Operatore Estrusione" },
    { v: "magazziniere", l: "Magazziniere" },
    { v: "segreteria", l: "Segreteria" },
];
const ROLE_TONE = {
    admin: "bg-red-500/15 text-red-300 border-red-500/40",
    ufficio: "bg-amber-500/15 text-amber-300 border-amber-500/40",
    operatore: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
    operatore_estrusione: "bg-sky-500/15 text-sky-300 border-sky-500/40",
    magazziniere: "bg-blue-500/15 text-blue-300 border-blue-500/40",
    segreteria: "bg-violet-500/15 text-violet-300 border-violet-500/40",
};

const emptyForm = { email: "", codice: "", name: "", password: "", role: "operatore" };

export default function Utenti() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try { const { data } = await api.get("/users"); setItems(data); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const isAdminRole = form.role === "admin";

    const save = async () => {
        try {
            const payload = {
                name: form.name, password: form.password, role: form.role,
                email: form.email || null,
                codice: isAdminRole ? null : (form.codice || null),
            };
            await api.post("/users", payload);
            toast.success("Utente creato");
            setOpen(false); setForm(emptyForm); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const changeRole = async (u, role) => {
        try {
            await api.put(`/users/${u.id}/role?role=${role}`);
            toast.success(`Ruolo aggiornato per ${u.email || u.codice}`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    // Reset password
    const [resetOpen, setResetOpen] = useState(false);
    const [resetUser, setResetUser] = useState(null);
    const [resetMode, setResetMode] = useState("auto"); // "auto" | "manual"
    const [resetManualPwd, setResetManualPwd] = useState("");
    const [resetResult, setResetResult] = useState(null); // {password, ttl}
    const [resetBusy, setResetBusy] = useState(false);
    const [copied, setCopied] = useState(false);

    const openReset = (u) => {
        setResetUser(u); setResetMode("auto"); setResetManualPwd("");
        setResetResult(null); setCopied(false); setResetOpen(true);
    };
    const doReset = async () => {
        setResetBusy(true);
        try {
            const payload = resetMode === "manual" ? { password: resetManualPwd } : {};
            const { data } = await api.post(`/users/${resetUser.id}/reset-password`, payload);
            setResetResult(data);
            // 60s auto-close della password
            setTimeout(() => setResetResult(null), 60000);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setResetBusy(false); }
    };
    const doCopy = async () => {
        try { await navigator.clipboard.writeText(resetResult.password); setCopied(true); toast.success("Password copiata"); }
        catch { toast.error("Copia manuale non riuscita"); }
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                    <div className="text-[10px] tracking-[0.3em] font-mono text-red-400/80">ADMIN</div>
                    <h1 className="mt-2 font-display text-3xl md:text-4xl font-extrabold">Utenti & Ruoli</h1>
                    <p className="mt-2 text-slate-400 text-sm">Operatori e ruoli non-admin accedono con <span className="text-amber-300 font-mono">Codice 4 cifre</span> + password. Solo l'Admin usa l'e-mail.</p>
                </div>
                <Button onClick={()=>setOpen(true)} data-testid="button-new-user"
                        className="bg-amber-500 hover:bg-amber-400 text-slate-950 gap-2">
                    <Plus size={16} /> Nuovo utente
                </Button>
            </div>

            <Card className="card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-800">
                            <tr>
                                <th className="text-left px-5 py-3">Codice / E-mail</th>
                                <th className="text-left px-5 py-3">Nome</th>
                                <th className="text-left px-5 py-3">Ruolo</th>
                                <th className="text-right px-5 py-3">Azioni</th>
                            </tr>
                        </thead>
                        <tbody data-testid="table-users">
                            {loading && <tr><td colSpan={4} className="px-5 py-8 text-center text-slate-500 text-xs">Caricamento…</td></tr>}
                            {!loading && items.length === 0 && (
                                <tr><td colSpan={4} className="px-5 py-12 text-center text-slate-500">
                                    <Users size={36} className="mx-auto mb-3 opacity-40" />
                                    <div className="text-sm">Nessun utente</div>
                                </td></tr>
                            )}
                            {items.map(u => (
                                <tr key={u.id} className="border-b border-slate-800/60 hover:bg-slate-800/30" data-testid={`row-user-${u.codice || u.email}`}>
                                    <td className="px-5 py-3">
                                        {u.codice ? (
                                            <span className="inline-flex items-center gap-2 font-mono text-amber-300 text-base tracking-widest">
                                                <Hash size={14} />{u.codice}
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-2 text-slate-200">
                                                <Mail size={14} className="text-slate-500" />{u.email}
                                            </span>
                                        )}
                                        {u.codice && u.email && <div className="text-[10px] text-slate-500 mt-1">{u.email}</div>}
                                    </td>
                                    <td className="px-5 py-3 text-slate-400">{u.name}</td>
                                    <td className="px-5 py-3">
                                        <Badge className={`badge-pill ${ROLE_TONE[u.role]}`}>{ROLES.find(r=>r.v===u.role)?.l || u.role}</Badge>
                                    </td>
                                    <td className="px-5 py-3 text-right">
                                        <div className="inline-flex items-center gap-2 justify-end">
                                            {u.role !== "admin" && (
                                                <Button size="sm" variant="outline" onClick={()=>openReset(u)}
                                                        data-testid={`button-reset-pwd-${u.codice || u.email}`}
                                                        className="bg-transparent border-red-500/40 text-red-300 hover:bg-red-500/10 gap-1 h-8 text-xs">
                                                    <KeyRound size={12} /> Reset password
                                                </Button>
                                            )}
                                            <Select value={u.role} onValueChange={v=>changeRole(u, v)}>
                                                <SelectTrigger data-testid={`select-role-${u.codice || u.email}`} className="bg-slate-950/50 border-slate-800 w-52"><SelectValue /></SelectTrigger>
                                                <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                                                    {ROLES.map(r => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
                    <DialogHeader><DialogTitle className="font-display">Nuovo utente</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label className="text-xs text-slate-400">Ruolo *</Label>
                            <Select value={form.role} onValueChange={v=>setForm({...form, role: v})}>
                                <SelectTrigger data-testid="select-user-role" className="bg-slate-950/50 border-slate-800 mt-1"><SelectValue /></SelectTrigger>
                                <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                                    {ROLES.map(r => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>

                        {isAdminRole ? (
                            <div>
                                <Label className="text-xs text-slate-400">E-mail *</Label>
                                <Input data-testid="input-user-email" type="email" value={form.email}
                                       onChange={e=>setForm({...form, email: e.target.value})}
                                       className="bg-slate-950/50 border-slate-800 mt-1" />
                                <div className="text-[10px] text-slate-500 mt-1">L'admin accede con e-mail.</div>
                            </div>
                        ) : (
                            <div>
                                <Label className="text-xs text-slate-400">Codice utente (4 cifre)</Label>
                                <Input data-testid="input-user-codice" inputMode="numeric" pattern="\d*"
                                       maxLength={4} value={form.codice}
                                       onChange={e=>setForm({...form, codice: e.target.value.replace(/\D/g,"")})}
                                       placeholder="es. 0007 — lascia vuoto per auto"
                                       className="bg-slate-950/50 border-slate-800 mt-1 font-mono tracking-widest" />
                                <div className="text-[10px] text-slate-500 mt-1">Lascia vuoto per assegnazione automatica del prossimo libero.</div>
                            </div>
                        )}

                        <div>
                            <Label className="text-xs text-slate-400">Nome *</Label>
                            <Input data-testid="input-user-name" value={form.name}
                                   onChange={e=>setForm({...form, name: e.target.value})}
                                   className="bg-slate-950/50 border-slate-800 mt-1" />
                        </div>
                        <div>
                            <Label className="text-xs text-slate-400">Password iniziale *</Label>
                            <Input data-testid="input-user-password" type="text" value={form.password}
                                   onChange={e=>setForm({...form, password: e.target.value})}
                                   placeholder="min. 4 caratteri"
                                   className="bg-slate-950/50 border-slate-800 mt-1" />
                            <div className="text-[10px] text-slate-500 mt-1">Comunica la password all'utente fuori dal sistema.</div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={()=>setOpen(false)} className="bg-transparent border-slate-700">Annulla</Button>
                        <Button onClick={save} data-testid="button-save-user" className="bg-amber-500 hover:bg-amber-400 text-slate-950">Crea</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <Dialog open={resetOpen} onOpenChange={setResetOpen}>
                <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-md">
                    <DialogHeader><DialogTitle className="font-display">Reimposta password</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        {resetUser && (
                            <div className="p-3 rounded-md bg-slate-950 border border-slate-800 text-sm">
                                <span className="font-mono text-amber-300">{resetUser.codice || resetUser.email}</span>
                                <span className="text-slate-400 mx-2">·</span>
                                <span>{resetUser.name}</span>
                            </div>
                        )}
                        {!resetResult && (
                            <>
                                <div className="flex gap-2">
                                    <button data-testid="tab-reset-auto"
                                        onClick={()=>setResetMode("auto")}
                                        className={`flex-1 py-2 text-xs font-semibold rounded-md ${resetMode==="auto" ? "bg-amber-500 text-slate-950" : "bg-slate-800 text-slate-400"}`}>
                                        Auto-genera
                                    </button>
                                    <button data-testid="tab-reset-manual"
                                        onClick={()=>setResetMode("manual")}
                                        className={`flex-1 py-2 text-xs font-semibold rounded-md ${resetMode==="manual" ? "bg-amber-500 text-slate-950" : "bg-slate-800 text-slate-400"}`}>
                                        Digita a mano
                                    </button>
                                </div>
                                {resetMode === "manual" && (
                                    <div>
                                        <Label className="text-xs text-slate-400">Password temporanea</Label>
                                        <Input data-testid="input-reset-manual-pwd" value={resetManualPwd}
                                               onChange={e=>setResetManualPwd(e.target.value)}
                                               placeholder="min. 4 caratteri"
                                               className="bg-slate-950/50 border-slate-800 mt-1 font-mono" />
                                    </div>
                                )}
                                <div className="text-[10px] text-slate-500">
                                    Al prossimo login l'utente dovrà obbligatoriamente impostare una nuova password.
                                </div>
                            </>
                        )}
                        {resetResult && (
                            <div className="p-4 rounded-md bg-emerald-500/10 border border-emerald-500/40 space-y-3">
                                <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-mono">
                                    Nuova password (visibile 60 secondi)
                                </div>
                                <div className="flex items-center gap-2">
                                    <code data-testid="reset-result-password" className="flex-1 px-3 py-3 rounded bg-slate-950 border border-slate-800 font-mono text-xl tracking-wider text-amber-300 select-all">
                                        {resetResult.password}
                                    </code>
                                    <Button onClick={doCopy} data-testid="button-copy-pwd"
                                            className="h-12 gap-1 bg-emerald-500 hover:bg-emerald-400 text-slate-950">
                                        {copied ? <Check size={14} /> : <Copy size={14} />} Copia
                                    </Button>
                                </div>
                                <div className="text-[11px] text-slate-400">
                                    Comunica la password a <b>{resetResult.user}</b> fuori dal sistema. Dopo la chiusura di questa finestra la password non è più recuperabile.
                                </div>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        {!resetResult ? (
                            <>
                                <Button variant="outline" onClick={()=>setResetOpen(false)} className="bg-transparent border-slate-700">Annulla</Button>
                                <Button onClick={doReset} disabled={resetBusy || (resetMode==="manual" && resetManualPwd.length < 4)}
                                        data-testid="button-do-reset"
                                        className="bg-red-500 hover:bg-red-400 text-white">
                                    {resetBusy ? "Attendere…" : "Reimposta"}
                                </Button>
                            </>
                        ) : (
                            <Button onClick={()=>{setResetOpen(false); setResetResult(null);}} data-testid="button-close-reset"
                                    className="bg-amber-500 hover:bg-amber-400 text-slate-950">Chiudi</Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
